import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, CheckCircle, AlertTriangle, XCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
function authH() { return { Authorization: `Bearer ${getToken()}` }; }

const STATUS_META = {
    ok:             { label: 'OK',        cls: 'text-emerald-400', Icon: CheckCircle },
    uncertain:      { label: '?',         cls: 'text-amber-400',   Icon: AlertTriangle },
    mismatch:       { label: 'WRONG',     cls: 'text-red-400',     Icon: XCircle },
    mapped_no_name: { label: 'mapped',    cls: 'text-muted-foreground', Icon: CheckCircle },
    unmapped:       { label: 'unmapped',  cls: 'text-muted-foreground', Icon: null },
};

function StatusBadge({ status }) {
    const m = STATUS_META[status] || STATUS_META.unmapped;
    return (
        <span className={`text-[10px] font-bold uppercase tracking-wide ${m.cls}`}>
            {m.label}
        </span>
    );
}

function PlayerRow({ player, statsapiRoster, onSaved }) {
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    async function selectMapping(sp) {
        setSaving(true);
        try {
            await axios.post(
                `${API_URL}/api/admin/football/worldcup/save-player-mapping`,
                {
                    pa_player_id:       player.pa_player_id,
                    pa_name:            player.name,
                    pa_team_id:         player.pa_team_id,
                    jersey:             player.shirt,
                    statsapi_player_id: sp ? sp.id : '',
                    statsapi_name:      sp ? sp.name : '',
                },
                { headers: authH() }
            );
            toast.success(sp ? `${player.name} → ${sp.name}` : `Cleared mapping for ${player.name}`);
            onSaved(player.pa_player_id, sp);
            setOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSaving(false);
        }
    }

    const rowBg = player.status === 'ok' ? '' :
                  player.status === 'uncertain' ? 'bg-amber-500/5' :
                  player.status === 'mismatch' ? 'bg-red-500/8' : '';

    return (
        <div className={`border-b last:border-0 ${rowBg}`} style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
            {/* Main row */}
            <div className="flex items-center gap-3 px-3 py-2.5">
                {/* Squad # */}
                <span className="w-7 text-center text-xs font-mono font-bold text-muted-foreground flex-shrink-0">
                    {player.shirt || '—'}
                </span>

                {/* PA name + position */}
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{player.name}</p>
                    {player.position && (
                        <p className="text-[10px] text-muted-foreground">{player.position}</p>
                    )}
                </div>

                {/* Arrow */}
                <span className="text-muted-foreground/40 text-sm flex-shrink-0">→</span>

                {/* StatsAPI mapping */}
                <div className="flex-1 min-w-0 text-right">
                    {player.statsapi_player_id ? (
                        <>
                            <p className={`text-sm font-medium truncate ${player.status === 'mismatch' ? 'text-red-400' : player.status === 'uncertain' ? 'text-amber-400' : ''}`}>
                                {player.statsapi_name || player.statsapi_player_id}
                            </p>
                            <p className="text-[10px] font-mono text-muted-foreground truncate">
                                {player.statsapi_player_id}
                                {player.manual && <span className="ml-1 text-cyan-400">manual</span>}
                            </p>
                        </>
                    ) : (
                        <p className="text-sm text-muted-foreground/50 italic">not mapped</p>
                    )}
                </div>

                {/* Status + Fix button */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <StatusBadge status={player.status} />
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2 gap-1 text-xs"
                        onClick={() => setOpen(v => !v)}
                        disabled={saving}
                    >
                        {saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />}
                        Fix
                    </Button>
                </div>
            </div>

            {/* Roster picker dropdown */}
            {open && (
                <div className="mx-3 mb-2 rounded-lg border overflow-hidden" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                    <div className="px-3 py-1.5 text-[10px] text-muted-foreground bg-muted/30 font-medium uppercase tracking-wide">
                        Pick StatsAPI player for <span className="text-foreground">{player.name}</span>
                    </div>
                    <div className="max-h-56 overflow-y-auto">
                        {/* Clear option */}
                        <button
                            className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/40 transition-colors border-b"
                            style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                            onClick={() => selectMapping(null)}
                        >
                            <span className="text-xs text-red-400/70 italic">— clear mapping —</span>
                        </button>
                        {statsapiRoster.map(sp => {
                            const isCurrent = sp.id === player.statsapi_player_id;
                            return (
                                <button
                                    key={sp.id}
                                    className={`w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/40 transition-colors border-b last:border-0 ${isCurrent ? 'bg-emerald-500/10' : ''}`}
                                    style={{ borderColor: 'rgba(255,255,255,0.04)' }}
                                    onClick={() => selectMapping(sp)}
                                >
                                    <div className="min-w-0">
                                        <p className={`text-xs font-medium truncate ${isCurrent ? 'text-emerald-400' : ''}`}>{sp.name}</p>
                                        <p className="text-[10px] text-muted-foreground">{sp.position}{sp.nationality ? ` · ${sp.nationality}` : ''}</p>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                        <p className="text-[10px] font-mono text-muted-foreground">{sp.id}</p>
                                        {sp.jersey && <p className="text-[10px] text-muted-foreground">club #{sp.jersey}</p>}
                                    </div>
                                </button>
                            );
                        })}
                        {statsapiRoster.length === 0 && (
                            <p className="text-xs text-muted-foreground text-center py-3">No StatsAPI roster available</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function AdminWCMappingReviewPage() {
    const [teams, setTeams] = useState([]);
    const [selectedTeamId, setSelectedTeamId] = useState('');
    const [reviewData, setReviewData] = useState(null);
    const [loadingTeams, setLoadingTeams] = useState(true);
    const [loadingReview, setLoadingReview] = useState(false);
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        axios.get(`${API_URL}/api/admin/football/worldcup/teams-list`, { headers: authH() })
            .then(r => setTeams(r.data.teams || []))
            .catch(() => toast.error('Failed to load teams'))
            .finally(() => setLoadingTeams(false));
    }, []);

    const fetchReview = useCallback(async (teamId) => {
        if (!teamId) return;
        setLoadingReview(true);
        setReviewData(null);
        try {
            const r = await axios.get(
                `${API_URL}/api/admin/football/worldcup/team-mapping-review/${teamId}`,
                { headers: authH() }
            );
            setReviewData({ ...r.data, pa_team_id: teamId });
        } catch {
            toast.error('Failed to load team review');
        } finally {
            setLoadingReview(false);
        }
    }, []);

    function handleTeamChange(e) {
        setSelectedTeamId(e.target.value);
        setFilterStatus('all');
        fetchReview(e.target.value);
    }

    function handlePlayerSaved(paPlayerId, sp) {
        setReviewData(prev => {
            if (!prev) return prev;
            const squad = prev.squad.map(p => {
                if (p.pa_player_id !== paPlayerId) return p;
                if (!sp) return { ...p, statsapi_player_id: '', statsapi_name: '', manual: false, status: 'unmapped' };
                const newStatus = sp.name.toLowerCase().replace(/[^a-z ]/g,'') ===
                    p.name.toLowerCase().replace(/[^a-z ]/g,'') ? 'ok' : 'uncertain';
                return { ...p, statsapi_player_id: sp.id, statsapi_name: sp.name, manual: true, status: newStatus };
            });
            const ok       = squad.filter(p => p.status === 'ok').length;
            const problems = squad.filter(p => ['mismatch','uncertain','unmapped'].includes(p.status)).length;
            return { ...prev, squad, summary: { ...prev.summary, ok, problems } };
        });
    }

    const visibleSquad = reviewData?.squad?.filter(p =>
        filterStatus === 'all' ||
        (filterStatus === 'problems' && ['mismatch', 'uncertain', 'unmapped'].includes(p.status)) ||
        p.status === filterStatus
    ) ?? [];

    const selectedTeamName = teams.find(t => t.pa_team_id === selectedTeamId)?.name || '';

    return (
        <div className="container max-w-3xl mx-auto py-6 px-4 space-y-5">
            {/* Header */}
            <div className="flex items-center gap-3">
                <Link to="/admin/football">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                        <ArrowLeft className="h-4 w-4" />
                    </Button>
                </Link>
                <div>
                    <h1 className="text-2xl font-bold page-title">Player Mapping Review</h1>
                    <p className="text-sm text-muted-foreground">
                        PA squad vs StatsAPI — spot and fix wrong mappings team by team
                    </p>
                </div>
            </div>

            {/* Team selector */}
            <div className="flex gap-3 items-center">
                {loadingTeams ? (
                    <Skeleton className="h-10 w-64" />
                ) : (
                    <select
                        className="rounded-md border bg-card text-sm px-3 py-2 text-foreground min-w-[220px]"
                        value={selectedTeamId}
                        onChange={handleTeamChange}
                    >
                        <option value="">— select a team —</option>
                        {teams.map(t => (
                            <option key={t.pa_team_id} value={t.pa_team_id}>
                                {t.name}{!t.statsapi_team_id ? ' (no StatsAPI mapping)' : ''}
                            </option>
                        ))}
                    </select>
                )}
                {reviewData && (
                    <Button variant="outline" size="sm" className="gap-2"
                        onClick={() => fetchReview(selectedTeamId)} disabled={loadingReview}>
                        <RefreshCw className={`h-3.5 w-3.5 ${loadingReview ? 'animate-spin' : ''}`} />
                        Refresh
                    </Button>
                )}
            </div>

            {/* Summary bar */}
            {reviewData && (
                <div className="flex gap-3">
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-xl font-bold">{reviewData.summary.total}</p>
                        <p className="text-xs text-muted-foreground">Squad size</p>
                    </div>
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-xl font-bold text-emerald-400">{reviewData.summary.ok}</p>
                        <p className="text-xs text-muted-foreground">Correct</p>
                    </div>
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className={`text-xl font-bold ${reviewData.summary.problems > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                            {reviewData.summary.problems}
                        </p>
                        <p className="text-xs text-muted-foreground">Need attention</p>
                    </div>
                    <div className="flex-1 rounded-lg border bg-card p-3 text-center">
                        <p className="text-xl font-bold text-muted-foreground">{reviewData.statsapi_roster.length}</p>
                        <p className="text-xs text-muted-foreground">StatsAPI roster</p>
                    </div>
                </div>
            )}

            {/* Filter tabs */}
            {reviewData && (
                <div className="flex gap-1 flex-wrap">
                    {[
                        { value: 'all',      label: `All (${reviewData.squad.length})` },
                        { value: 'problems', label: `Problems (${reviewData.summary.problems})` },
                        { value: 'ok',       label: `OK (${reviewData.summary.ok})` },
                        { value: 'mismatch', label: `Wrong (${reviewData.squad.filter(p=>p.status==='mismatch').length})` },
                        { value: 'uncertain',label: `Uncertain (${reviewData.squad.filter(p=>p.status==='uncertain').length})` },
                        { value: 'unmapped', label: `Unmapped (${reviewData.squad.filter(p=>p.status==='unmapped').length})` },
                    ].map(tab => (
                        <button key={tab.value}
                            onClick={() => setFilterStatus(tab.value)}
                            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                filterStatus === tab.value
                                    ? 'bg-primary text-primary-foreground'
                                    : 'bg-muted text-muted-foreground hover:text-foreground'
                            }`}>
                            {tab.label}
                        </button>
                    ))}
                </div>
            )}

            {/* Legend */}
            {reviewData && (
                <div className="flex gap-4 text-[10px] text-muted-foreground flex-wrap">
                    <span><span className="text-emerald-400 font-bold">OK</span> — names match well</span>
                    <span><span className="text-amber-400 font-bold">?</span> — names differ, check manually</span>
                    <span><span className="text-red-400 font-bold">WRONG</span> — names don't match at all</span>
                    <span><span className="text-muted-foreground font-bold">unmapped</span> — no StatsAPI link yet</span>
                </div>
            )}

            {/* Loading */}
            {loadingReview && (
                <div className="space-y-2">
                    {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
            )}

            {/* Player list */}
            {!loadingReview && reviewData && (
                <div className="rounded-lg border overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center gap-3 px-3 py-2 bg-muted/50 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-b">
                        <span className="w-7 text-center">#</span>
                        <span className="flex-1">PA Player</span>
                        <span className="text-muted-foreground/40">→</span>
                        <span className="flex-1 text-right">StatsAPI Player</span>
                        <span className="w-24 text-right">Status</span>
                    </div>

                    {visibleSquad.length === 0 && (
                        <p className="text-center text-sm text-muted-foreground py-8">
                            {filterStatus === 'problems' ? 'No problems found — all players look correct!' : 'No players match this filter.'}
                        </p>
                    )}

                    {visibleSquad.map(player => (
                        <PlayerRow
                            key={player.pa_player_id}
                            player={{ ...player, pa_team_id: reviewData.pa_team_id }}
                            statsapiRoster={reviewData.statsapi_roster}
                            onSaved={handlePlayerSaved}
                        />
                    ))}
                </div>
            )}

            {/* Empty state — no team selected */}
            {!loadingReview && !reviewData && !loadingTeams && (
                <div className="rounded-lg border bg-card py-16 text-center text-muted-foreground">
                    <p className="font-medium">Select a team above to review mappings</p>
                    <p className="text-sm mt-1">Shows every PA squad player vs their current StatsAPI mapping</p>
                </div>
            )}
        </div>
    );
}
